/**
 * Typography Tokens - Sistema de Diseño UNMSM
 *
 * Sistema tipográfico completo: familias, tamaños, pesos, alturas de línea y espaciados.
 * Basado en escala modular con ratio de 1.125 (Major Second).
 *
 * @module TypographyTokens
 */

/**
 * Sistema tipográfico basado en escala modular
 * Tamaños calculados con ratio de 1.125 (Major Second)
 */
export const TYPOGRAPHY = {
  /**
   * Familias tipográficas
   */
  fontFamily: {
    /** Fuente base del sistema (Roboto) */
    base: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    /** Fuente para encabezados */
    heading: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
    /** Fuente monoespaciada para código */
    mono: '"Roboto Mono", "Courier New", Courier, monospace',
  },

  /**
   * Tamaños de fuente
   * Escala: 12px, 14px, 16px, 18px, 20px, 24px, 30px, 36px, 48px, 60px, 72px
   *
   * @example
   * ```typescript
   * // En componentes Vue
   * <style scoped>
   * .title {
   *   font-size: v-bind(TYPOGRAPHY.fontSize['3xl']);
   * }
   * </style>
   * ```
   */
  fontSize: {
    /** 12px - Microcopias, notas al pie */
    xs: '0.75rem',
    /** 14px - Texto pequeño, labels secundarios */
    sm: '0.875rem',
    /** 16px - Texto base del cuerpo */
    base: '1rem',
    /** 18px - Texto destacado, subtítulos */
    lg: '1.125rem',
    /** 20px - Subtítulos, leads */
    xl: '1.25rem',
    /** 24px - Títulos pequeños (h4, h5) */
    '2xl': '1.5rem',
    /** 30px - Títulos medianos (h3) */
    '3xl': '1.875rem',
    /** 36px - Títulos grandes (h2) */
    '4xl': '2.25rem',
    /** 48px - Títulos principales (h1) */
    '5xl': '3rem',
    /** 60px - Display grande */
    '6xl': '3.75rem',
    /** 72px - Display extra grande */
    '7xl': '4.5rem',
  },

  /**
   * Pesos tipográficos
   * Roboto soporta: 100, 300, 400, 500, 700, 900
   */
  fontWeight: {
    /** 100 - Thin */
    thin: 100,
    /** 300 - Light */
    light: 300,
    /** 400 - Regular (peso base) */
    normal: 400,
    /** 500 - Medium */
    medium: 500,
    /** 700 - Bold */
    bold: 700,
    /** 900 - Black */
    black: 900,
  },

  /**
   * Alturas de línea
   * Calculadas para óptima legibilidad
   */
  lineHeight: {
    /** 1 - Sin espacio adicional (para íconos) */
    none: 1,
    /** 1.25 - Títulos ajustados */
    tight: 1.25,
    /** 1.375 - Títulos normales */
    snug: 1.375,
    /** 1.5 - Texto normal */
    normal: 1.5,
    /** 1.625 - Texto relajado */
    relaxed: 1.625,
    /** 2 - Texto muy espaciado */
    loose: 2,
  },

  /**
   * Espaciado entre letras (letter-spacing)
   */
  letterSpacing: {
    /** -0.05em - Muy ajustado (títulos grandes) */
    tighter: '-0.05em',
    /** -0.025em - Ajustado */
    tight: '-0.025em',
    /** 0 - Normal */
    normal: '0',
    /** 0.025em - Amplio */
    wide: '0.025em',
    /** 0.05em - Muy amplio */
    wider: '0.05em',
    /** 0.1em - Extra amplio (microcopias en mayúsculas) */
    widest: '0.1em',
  },
} as const

/**
 * Tipo para tamaños de fuente
 */
export type FontSize = keyof typeof TYPOGRAPHY.fontSize

/**
 * Tipo para pesos de fuente
 */
export type FontWeight = keyof typeof TYPOGRAPHY.fontWeight

/**
 * Tipo para alturas de línea
 */
export type LineHeight = keyof typeof TYPOGRAPHY.lineHeight

/**
 * Tipo para espaciado entre letras
 */
export type LetterSpacing = keyof typeof TYPOGRAPHY.letterSpacing
